import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ModalBasic from "@/components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";
import { addToast } from "@/store/app/action.js";
import { updateAdminProduct } from "@/api/masterMenu.js";
import { getManageProductDetails } from "@/api/admin.js";
import { getProductTags } from "@/api/productTags.js";
import ProductAttributeTagSelect from "./ProductAttributeTagSelect.jsx";

const emptyForm = () => ({
  title: "",
  subtitle: "",
  company: "",
  description: "",
  volume_label: "",
  upc_code: "",
  image: "",
  image_large: "",
  additional_images: [],
  abv: "",
  avatax_code: "",
  no_tax: false,
  disabled: false,
  attribute_tags: [],
});

function productToForm(p) {
  if (!p || typeof p !== "object") return emptyForm();
  return {
    title: String(p.title || ""),
    subtitle: String(p.subtitle || ""),
    company: String(p.company || ""),
    description: String(p.description || ""),
    volume_label: String(p.volume_label || p.volume || ""),
    upc_code: String(p.upc_code || ""),
    image: String(p.image || ""),
    image_large: String(p.image_large || ""),
    additional_images: Array.isArray(p.additional_images)
      ? p.additional_images.map((u) => String(u || "").trim()).filter(Boolean)
      : [],
    abv: p.abv === undefined || p.abv === null ? "" : String(p.abv),
    avatax_code: String(p.avatax_code || ""),
    no_tax: Boolean(p.no_tax),
    disabled: Boolean(p.disabled),
    attribute_tags: Array.isArray(p.attribute_tags)
      ? p.attribute_tags.map((t) => String(t || "").trim()).filter(Boolean)
      : [],
  };
}

function MasterMenuEditProduct({ sku, isOpen, setIsOpen, onSaved }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [product, setProduct] = useState(null);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    if (!isOpen || !sku) return;
    let cancelled = false;

    setLoading(true);
    getManageProductDetails(sku)
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
        setForm(productToForm(p));
      })
      .catch((error) => {
        if (cancelled) return;
        dispatch(addToast({
          type: "error",
          message: error.response?.data?.message || "Failed to load product",
          timer: 6,
        }));
        setIsOpen(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    setTagsLoading(true);
    getProductTags()
      .then((tags) => {
        if (!cancelled) setTagOptions(Array.isArray(tags) ? tags : []);
      })
      .catch(() => {
        if (!cancelled) setTagOptions([]);
      })
      .finally(() => {
        if (!cancelled) setTagsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, sku, dispatch, setIsOpen]);

  const title = useMemo(() => {
    const name = [product?.title, product?.subtitle].filter(Boolean).join(" ");
    return name ? `Edit — ${name}` : "Edit product";
  }, [product]);

  const handleSave = async () => {
    if (!sku) return;
    if (!form.title.trim()) {
      dispatch(addToast({ type: "error", message: "Name is required", timer: 4 }));
      return;
    }

    const payload = {
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      company: form.company.trim(),
      description: form.description.trim(),
      volume_label: form.volume_label.trim(),
      upc_code: form.upc_code.trim(),
      image: form.image.trim(),
      image_large: form.image_large.trim(),
      additional_images: form.additional_images,
      avatax_code: form.avatax_code.trim(),
      no_tax: Boolean(form.no_tax),
      disabled: Boolean(form.disabled),
      attribute_tags: form.attribute_tags,
    };

    const abvRaw = String(form.abv || "").trim();
    if (abvRaw) {
      const n = parseFloat(abvRaw.replace(/%$/, "").trim());
      payload.abv = Number.isFinite(n) ? n : abvRaw;
    }

    try {
      setSaving(true);
      await updateAdminProduct(sku, payload);
      dispatch(addToast({ type: "success", message: `Saved (${sku})`, timer: 4 }));
      onSaved?.();
      setIsOpen(false);
    } catch (error) {
      dispatch(addToast({
        type: "error",
        message: error.response?.data?.message || error.message || "Failed to save product",
        timer: 8,
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBasic
      id="master-menu-edit-product"
      title={title}
      modalOpen={isOpen}
      setModalOpen={(open) => {
        if (!open) setIsOpen(false);
      }}
      width="4xl"
    >
      {loading ? (
        <div className="px-5 py-10 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2" />
          <p className="text-sm text-slate-600 dark:text-slate-400">Loading product...</p>
        </div>
      ) : (
        <div className="px-5 py-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-500 dark:text-slate-400">
              <span className="font-semibold">SKU:</span> {sku}
            </div>
            <ToggleSwitch
              checked={Boolean(form.disabled)}
              onChange={(checked) => setField("disabled", checked)}
              label="Globally disabled"
              onLabel="Globally disabled"
              offLabel="Globally disabled"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                className="form-input w-full"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Brand</label>
              <input
                className="form-input w-full"
                value={form.company}
                onChange={(e) => setField("company", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <input
                className="form-input w-full"
                value={form.subtitle}
                onChange={(e) => setField("subtitle", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Volume</label>
              <input
                className="form-input w-full"
                value={form.volume_label}
                onChange={(e) => setField("volume_label", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">UPC</label>
              <input
                className="form-input w-full font-mono"
                inputMode="numeric"
                autoComplete="off"
                value={form.upc_code}
                onChange={(e) => setField("upc_code", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">ABV</label>
              <input
                className="form-input w-full"
                inputMode="decimal"
                autoComplete="off"
                placeholder="e.g. 5.2"
                value={form.abv}
                onChange={(e) => setField("abv", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Avalara tax code</label>
              <input
                className="form-input w-full font-mono text-sm"
                value={form.avatax_code}
                onChange={(e) => setField("avatax_code", e.target.value)}
                placeholder="e.g. P0000000"
                autoComplete="off"
              />
              <div className="mt-2">
                <ToggleSwitch
                  checked={Boolean(form.no_tax)}
                  onChange={(checked) => setField("no_tax", checked)}
                  label="No Tax"
                  onLabel="No Tax"
                  offLabel="No Tax"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Attribute Tags</label>
              <ProductAttributeTagSelect
                options={tagOptions}
                value={form.attribute_tags}
                onChange={(ids) => setField("attribute_tags", ids)}
                loading={tagsLoading}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="form-input w-full min-h-[120px] resize-y"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image (small) URL</label>
              <input
                className="form-input w-full text-sm"
                value={form.image}
                onChange={(e) => setField("image", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image (large) URL</label>
              <input
                className="form-input w-full text-sm"
                value={form.image_large}
                onChange={(e) => setField("image_large", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Additional images</label>
              <textarea
                className="form-input w-full min-h-[88px] resize-y text-sm font-mono"
                placeholder="One image or video URL per line"
                value={(form.additional_images || []).join("\n")}
                onChange={(e) => {
                  const urls = e.target.value
                    .split("\n")
                    .map((line) => line.trim())
                    .filter(Boolean);
                  setField("additional_images", urls);
                }}
              />
            </div>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            Category and type changes aren&apos;t available here.
          </p>

          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 dark:border-slate-700 pt-4">
            <SecondaryButton type="button" onClick={() => setIsOpen(false)} disabled={saving}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="button" onClick={handleSave} loading={saving} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </ModalBasic>
  );
}

export default MasterMenuEditProduct;
